// Settings → Time Slots (Wave E).
//
// Manages the booking-form's AM / AM-PM / PM labels per slot type
// (weekday + weekend). Two side-by-side lists with inline add / edit /
// delete. Uses /api/v1/collections/time-slots (existing migration 069 / 070 / 071).

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Table, type Column, type RowAction } from '../../../components/ui/Table/Table';
import { Input } from '../../../components/form/Input';
import { Badge } from '../../../components/ui/Badge/Badge';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateTimeSlot,
  useDeleteTimeSlot,
  useTimeSlotsByType,
  useUpdateTimeSlot,
} from './hooks/useTimeSlots';
import type { TimeSlotDto } from '../../../types/api';
import styles from './TimeSlotsPage.module.css';

interface TimeSlotsListProps {
  slotType: 'weekday' | 'weekend';
  title: string;
}

function TimeSlotsList({ slotType, title }: TimeSlotsListProps): JSX.Element {
  const list = useTimeSlotsByType(slotType);
  const create = useCreateTimeSlot();
  const update = useUpdateTimeSlot();
  const remove = useDeleteTimeSlot();

  const items = list.data ?? [];

  const [newLabel, setNewLabel] = useState('');
  const [newOrder, setNewOrder] = useState<number>(0);

  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editOrder, setEditOrder] = useState<number>(0);

  const [deleteTarget, setDeleteTarget] = useState<TimeSlotDto | null>(null);

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!newLabel.trim()) return;
    create.mutate(
      { slot_type: slotType, label: newLabel.trim(), display_order: newOrder },
      {
        onSuccess: () => {
          setNewLabel('');
          setNewOrder(items.length + 1);
        },
      },
    );
  }

  function startEdit(row: TimeSlotDto) {
    setEditId(row.id);
    setEditLabel(row.label);
    setEditOrder(row.display_order);
  }

  function saveEdit() {
    if (editId === null) return;
    update.mutate(
      { id: editId, input: { label: editLabel.trim(), display_order: editOrder } },
      { onSuccess: () => setEditId(null) },
    );
  }

  const columns: Column<TimeSlotDto>[] = [
    {
      key: 'label',
      header: 'Label',
      render: (_v, row) =>
        editId === row.id ? (
          <Input
            aria-label={`Edit label for ${row.label}`}
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
          />
        ) : (
          row.label
        ),
    },
    {
      key: 'display_order',
      header: 'Order',
      align: 'right',
      render: (_v, row) =>
        editId === row.id ? (
          <Input
            aria-label={`Edit order for ${row.label}`}
            variant="number"
            value={editOrder}
            onChange={(e) => setEditOrder(Number(e.target.value))}
          />
        ) : (
          row.display_order
        ),
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (_v, row) =>
        row.is_active ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="muted">Inactive</Badge>
        ),
    },
  ];

  const rowActions = (row: TimeSlotDto): RowAction<TimeSlotDto>[] =>
    editId === row.id
      ? [
          { label: 'Save', icon: Save, onClick: saveEdit, variant: 'primary' },
          {
            label: 'Cancel',
            icon: X,
            onClick: () => setEditId(null),
            variant: 'ghost',
          },
        ]
      : [
          {
            label: 'Edit',
            icon: Pencil,
            onClick: () => startEdit(row),
            variant: 'ghost',
          },
          {
            label: 'Delete',
            icon: Trash2,
            onClick: () => setDeleteTarget(row),
            variant: 'danger',
          },
        ];

  return (
    <Card title={title}>
      <form onSubmit={handleCreate} className={styles.addRow}>
        <Input
          label="Label"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          required
        />
        <Input
          label="Order"
          variant="number"
          value={newOrder}
          onChange={(e) => setNewOrder(Number(e.target.value))}
        />
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Add
        </Button>
      </form>

      <div className={styles.tableWrap}>
        <Table
          columns={columns}
          data={items}
          keyField={(row) => row.id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <p>No {slotType} slots yet.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel={title}
        />
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete time slot?"
        message={
          deleteTarget !== null
            ? `Delete "${deleteTarget.label}" (${slotType})? Existing bookings linked to this slot keep their assignment.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={remove.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          remove.mutate(deleteTarget.id, {
            onSuccess: () => {
              setDeleteTarget(null);
              void list.refetch();
            },
          });
        }}
      />
    </Card>
  );
}

export function TimeSlotsPage(): JSX.Element {
  const navigate = useNavigate();
  return (
    <div className={styles.page}>
      <PageHeader
        title="Time Slots"
        subtitle="Booking-form labels for each collection window. Configured per day type."
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings')}
          >
            Back
          </Button>
        }
      />

      <div className={styles.grid}>
        <TimeSlotsList slotType="weekday" title="Weekday Slots" />
        <TimeSlotsList slotType="weekend" title="Weekend Slots" />
      </div>
    </div>
  );
}
